import { Component, HostBinding, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { determineStorageUrl } from "../common/storage-tools";
import { RecipeSearchResult } from "../models/recipe-search-result.model";
import { ReferenceIntakeProfile } from "../models/reference-intake-profile";
import { Settings } from "../models/Settings";
import { FoodPortalStateService } from "../services/food-portal-state.service";
import { FoodPortalService } from "../services/food-portal.service";

@Component({
  selector: "pi-recipe-details",
  templateUrl: "./recipe-details.component.html",
  styleUrls: ["./recipe-details.component.scss"],
  standalone: false,
})
export class RecipeDetailsComponent implements OnInit, OnDestroy {
  recipeId: number;
  recipe: RecipeSearchResult;
  settings: Settings;
  destroy$ = new Subject<boolean>();
  containAllergens: string[] = [];
  mayContainAllergens: string[] = [];

  kjPer100 = 0;
  kcalPer100 = 0;
  carbPer100 = 0;
  sugarsPer100 = 0;
  fatPer100 = 0;
  satPer100 = 0;
  fibrePer100 = 0;
  saltPer100 = 0;
  proteinPer100 = 0;

  storageUrl: string;
  @HostBinding("class") hostClass;
  currentRip: ReferenceIntakeProfile; //current reference intake profile RIP
  ripsMenuVisible: boolean;

  constructor(
    private state: FoodPortalStateService,
    private router: Router,
    private route: ActivatedRoute,
    private svc: FoodPortalService,
  ) {}

  ngOnInit() {
    this.settings = this.state.portalSettings;
    this.recipeId = +this.route.snapshot.params.recipeId;
    this.recipe = this.state.recipes.find(x => x.id === this.recipeId);
    if (!this.recipe.photoUrl || !this.recipe.usePhoto || this.settings.disableRecipePhotos)
      this.hostClass = "no-photo";

    this.storageUrl = determineStorageUrl();

    Object.keys(this.recipe.allergens).forEach(key => {
      const allergenValue = this.recipe.allergens[key];
      if (allergenValue === 1 && this.recipe.allergens.isMajor(key)) {
        this.containAllergens.push(key);
      }

      if (allergenValue === 2 && this.recipe.allergens.isMajor(key)) {
        this.mayContainAllergens.push(key);
      }

      // nuts and gluten are displayed in the contains list if any of their sub-allergens are 'contains'
      // but they should ALSO be displayed in the may contain list if any of their sub-allergens are 'may contain'

      if (
        allergenValue === 2 &&
        this.recipe.allergens.nutsAllergens.includes(key) &&
        !this.mayContainAllergens.includes("nuts")
      ) {
        this.mayContainAllergens.push("nuts"); // add nuts if any of the nuts sub-allergens are present
      }

      if (
        allergenValue === 2 &&
        this.recipe.allergens.glutenAllergens.includes(key) &&
        !this.mayContainAllergens.includes("gluten")
      ) {
        this.mayContainAllergens.push("gluten"); // add gluten if any of the gluten sub-allergens are present
      }
    });

    if (this.recipe.portionSize) {
      this.kjPer100 = Math.round((this.recipe.nutrition.kj / this.recipe.portionSize) * 100); // 0dp
      this.kcalPer100 = Math.round((this.recipe.nutrition.kcal / this.recipe.portionSize) * 100); // 0dp
      this.fatPer100 = Math.round((this.recipe.nutrition.fat / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.carbPer100 = Math.round((this.recipe.nutrition.carbohydrate / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.sugarsPer100 =
        Math.round((this.recipe.nutrition.carbohydrateOfWhichSugars / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.satPer100 = Math.round((this.recipe.nutrition.fatOfWhichSaturates / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.fibrePer100 = Math.round((this.recipe.nutrition.fibre / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.proteinPer100 = Math.round((this.recipe.nutrition.protein / this.recipe.portionSize) * 1000) / 10; // 1dp
      this.saltPer100 = Math.round((this.recipe.nutrition.salt / this.recipe.portionSize) * 10000) / 100; // 2dp
    }

    this.state.referenceIntakeProfile.pipe(takeUntil(this.destroy$)).subscribe(x => {
      this.currentRip = x;
    });
  }

  /** Boolean value which indicates whether the CO2e badge feature should be displayed */
  public get showCo2eBadge(): boolean {
    // return true if the portal is set to display values
    return this.settings.showCo2eValues;
  }

  public getNutritionPercentage(field: string): number {
    // If the custom default system profile has been disabled and
    // no custom profiles have been configured we need to return an empty string
    // as the percentage calculation will not be possible

    if (!this.settings.referenceIntakeProfiles || this.settings.referenceIntakeProfiles.length === 0) return null;

    if (!field || !this.recipe.nutrition[field]) {
      return 0;
    }

    switch (field) {
      case "kj":
        return this.recipe.nutrition.kj / this.currentRip.kj;
      case "kcal":
        return this.recipe.nutrition.kcal / this.currentRip.kcal;
      case "fat":
        return this.recipe.nutrition.fat / this.currentRip.fat;
      case "fatOfWhichSaturates":
        return this.recipe.nutrition.fatOfWhichSaturates / this.currentRip.sats;
      case "carbohydrateOfWhichSugars":
        return this.recipe.nutrition.carbohydrateOfWhichSugars / this.currentRip.sugars;
      case "salt":
        return this.recipe.nutrition.sodium / this.currentRip.sodium;
      case "carbohydrate":
        return this.recipe.nutrition.carbohydrate / this.currentRip.carbs;
      case "fibre":
        return this.recipe.nutrition.fibre / this.currentRip.fibre;
      case "protein":
        return this.recipe.nutrition.protein / this.currentRip.protein;
      default:
        return 0;
    }
  }

  /** Returns true if the 'contains prohibited allergen' warning message should be displayed */
  showRecipeContainsAllergenWarning(): boolean {
    // if the recipe is set to hide allergens, return false
    if ((this.recipe.publicNutrition & 2) !== 2) return false;

    // return true if teh recipe has a 1 (contains) for for any of the user's allergies
    return this.svc.selectedAllergens.some(x => this.recipe.allergens[x.name] === 1);
  }

  /** Returns true if the 'may contain prohibited allergen' warning message should be displayed */
  showRecipeMayContainAllergenWarning(): boolean {
    // if the recipe is set to hide allergens, return false
    if ((this.recipe.publicNutrition & 2) !== 2) return false;

    // return true if the recipe has a 2 (may contain) for for any of the user's allergies
    return this.svc.selectedAllergens.some(x => this.recipe.allergens[x.name] === 2);
  }

  /** Returns true if the 'prohibited allergen data missing' warning message should be displayed */
  showRecipeMissingAllergensWarning(): boolean {
    // if the recipe is set to hide allergens, return false
    if ((this.recipe.publicNutrition & 2) !== 2) return false;

    // return true if the recipe has a 3 (missing data) for for any of the user's allergies
    return this.svc.selectedAllergens.some(x => this.recipe.allergens[x.name] === 3);
  }

  /**
   * Returns the displayname for the specified allergen short name
   * @param shortName
   */
  allergenName(shortName: string): string {
    return this.svc.allergenListV2.find(x => x.name === shortName).displayName;
  }

  /** Controls the visibility of the sub-allergen list for each allergen */
  public showSubAllergenList(allergenName: string, containsValue: number): boolean {
    if (allergenName === "gluten") return !!this.subAllergensByValue("gluten", containsValue);
    if (allergenName === "nuts") return !!this.subAllergensByValue("nuts", containsValue);

    // for other allergens return false
    return false;
  }

  /** Returns the list of sub-allergen values for gluten and nuts that match the provided contains value */
  public subAllergensByValue(subAllergenType: "gluten" | "nuts" | string, containsValue: number): string {
    return this.svc.allergenListV2
      .filter(x => x.parent === subAllergenType)
      .filter(x => this.recipe.allergens[x.name] === containsValue)
      .map(x => x.displayName)
      .join(", ");
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  goBack() {
    this.router.navigateByUrl("home");
  }

  showRips() {
    this.ripsMenuVisible = true;
  }

  selectRiProfile(profile: ReferenceIntakeProfile) {
    this.state.referenceIntakeProfile.next(profile);
    this.ripsMenuVisible = false;
  }

  openMenu() {
    this.state.openMenu$.next();
  }

  hasMultipleProfiles = () => this.settings.referenceIntakeProfiles.length > 1;

  public tagName = (tagId: number): string => this.state.allTags.find(x => x.id === tagId).name;

  public get showNutritionData(): boolean {
    return this.settings.showNutritionalInfo && this.recipe.showNutrientData;
  }
}
